// Pulse Power Quiz — Kaun Banega Cables-pati (KBC) hot-seat replica.
// Full-screen immersive flow:
//   reading (host narrates, no timer) → answering (timer + ticking) →
//   confirming ("LOCK KIYA JAYE?" popup) → result (CORRECT/WRONG popup + explanation).
// Multilingual: on-screen text + host voice switch to any major Indian language
// (default = English text + Hindi voice). Subtle arcade music on the level screen.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, mediaUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Trophy, Zap, CheckCircle2, XCircle, Lock, ChevronRight, RotateCcw,
    Award, Loader2, Crown, Volume2, VolumeX, LogOut,
} from 'lucide-react';
import mascot from '@/assets/voltmaster.jpg';
import { useCourseLang } from '@/lib/courseLang';


const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
// KBC "cylindrical hexagon" capsule: pointed left/right ends (Point 14 reference image10).
const HEX_Q = 'polygon(1.6% 0, 98.4% 0, 100% 50%, 98.4% 100%, 1.6% 100%, 0 50%)';
const HEX_O = 'polygon(3.2% 0, 96.8% 0, 100% 50%, 96.8% 100%, 3.2% 100%, 0 50%)';

// ---- Arcade colour palette (Colour Guide 2.0) — scoped to Pulse Power screens ONLY ----
const ARC = {
    green: '#00DB79', black: '#000000', white: '#FFFFFF',
    forest: '#013022', mint: '#9DF5CE', yellow: '#FFFF66',
    sky: '#30BDFE', red: '#FF5852', grass: '#2DAB00',
};
// Point 21: solid deep-forest background (#013022) across all Pulse Power screens.
const ARC_BG = '#013022';

const FALLBACK_PHRASES = {
    lock: 'Computer ji, lock it in?',
    correct: 'Congratulations! Well played, that is absolutely correct!',
    wrong: "Oh no! Sadly that was the wrong answer. Don't lose heart — keep playing!",
    timeout: 'Oh! Your time is up. No worries, keep playing!',
};

// ---- Ticking clock (Web Audio; no external asset) ----
let _audioCtx = null;
function getCtx() {
    if (typeof window === 'undefined') return null;
    if (!_audioCtx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) _audioCtx = new AC();
    }
    if (_audioCtx && _audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
    return _audioCtx;
}
function playTick(urgent = false) {
    const ctx = getCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = urgent ? 1400 : 900;
    gain.gain.setValueAtTime(urgent ? 0.16 : 0.09, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.07);
}


export default function PulsePowerQuiz({ game, onPlayed }) {
    const [overview, setOverview] = useState(null);
    const [mode, setMode] = useState('levels');
    const [session, setSession] = useState(null);
    const [muted, setMuted] = useState(false);
    // Splash video plays once per browser session (before the level-select screen).
    const [splash, setSplash] = useState(() => {
        try { return !sessionStorage.getItem('pulse-splash-seen'); } catch { return true; }
    });
    const endSplash = useCallback(() => {
        try { sessionStorage.setItem('pulse-splash-seen', '1'); } catch (_) {}
        setSplash(false);
    }, []);

    const loadLevels = useCallback(() => {
        api.get('/games/pulse-power/levels').then((r) => setOverview(r.data)).catch(() => setOverview({ group: 'A', time_limit_sec: 20, questions_per_level: 10, levels: [], my_total: 0, blazing_enabled: true, blazing_max: 4 }));
    }, []);
    useEffect(() => { loadLevels(); }, [loadLevels]);

    const startLevel = async (level) => {
        try {
            getCtx();
            const r = await api.post('/games/pulse-power/start', { level });
            setSession(r.data);
            setMode('play');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not start level');
        }
    };

    const onFinished = (summary) => {
        setSession((s) => ({ ...s, summary }));
        setMode('result');
        loadLevels();
        onPlayed && onPlayed();
    };

    if (!overview) return <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

    if (splash) return <SplashVideo onDone={endSplash} />;

    if (mode === 'play' && session) {
        return <PlayScreen session={session} muted={muted} setMuted={setMuted} mascotUrl={overview.mascot_url}
            onFinished={onFinished} onQuit={() => { setMode('levels'); loadLevels(); }} />;
    }
    if (mode === 'result' && session?.summary) {
        return <ResultScreen session={session} onReplay={() => startLevel(session.level)} onLevels={() => setMode('levels')} />;
    }
    return <LevelSelect overview={overview} onPick={startLevel} />;
}

function SplashVideo({ onDone }) {
    const vidRef = useRef(null);
    useEffect(() => {
        const v = vidRef.current;
        if (!v) return;
        // Try to play with sound; if the browser blocks autoplay-with-audio, retry muted.
        v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
        const safety = setTimeout(onDone, 20000);   // never trap the user
        return () => clearTimeout(safety);
    }, [onDone]);
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" style={{ background: ARC.black }} data-testid="pulse-splash">
            <video ref={vidRef} src="/pulse-splash.mp4" autoPlay playsInline onEnded={onDone}
                className="w-full h-full object-contain" data-testid="pulse-splash-video" />
            <button onClick={onDone} data-testid="pulse-splash-skip"
                className="absolute bottom-6 right-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-black shadow-lg transition active:scale-95"
                style={{ background: ARC.green, color: ARC.black }}>
                Skip <ChevronRight className="h-4 w-4" />
            </button>
        </div>
    );
}

function LevelSelect({ overview, onPick }) {
    return (
        <div className="rounded-3xl p-5 sm:p-6 text-white ring-1"
            style={{ background: ARC_BG, borderColor: 'rgba(0,219,121,0.25)', boxShadow: '0 0 0 1px rgba(0,219,121,0.25)' }}
            data-testid="pulse-level-select">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display font-extrabold text-xl flex items-center gap-2">
                    <Zap className="h-5 w-5" style={{ color: ARC.green }} /> Take the hot seat
                </h2>
                <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wide text-white/50 font-bold">Your best total</div>
                    <div className="font-display font-extrabold text-2xl" style={{ color: ARC.green }} data-testid="pulse-my-total">{overview.my_total} pts</div>
                </div>
            </div>

            {/* Level-select prompt (client copy) */}
            <p className="mt-2 text-sm sm:text-base font-semibold" style={{ color: ARC.mint }} data-testid="pulse-level-prompt">
                Please select which Level you want to Play?
            </p>

            <div className="grid grid-cols-1 gap-2.5 mt-5 max-w-md mx-auto">
                {overview.levels.map((lv) => (
                    <button
                        key={lv.level}
                        disabled={!lv.playable}
                        onClick={() => onPick(lv.level)}
                        data-testid={`pulse-level-${lv.level}`}
                        className={`relative rounded-2xl px-4 py-3 flex items-center justify-between gap-3 border-2 transition group ${lv.playable ? 'hover:shadow-lg active:scale-[0.99]' : 'cursor-not-allowed'}`}
                        style={lv.playable
                            ? { borderColor: 'rgba(0,219,121,0.45)', background: 'rgba(0,219,121,0.08)' }
                            : { borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="font-display font-extrabold text-2xl w-12 shrink-0 text-left">L{lv.level}</span>
                            <div className="text-left min-w-0">
                                <div className="inline-flex items-center gap-1 font-semibold text-sm" style={{ color: ARC.green }}>+{lv.points_per_question} <span className="text-white/40 font-normal">/ question</span></div>
                                <div className="text-[10px] text-white/40 mt-0.5">{lv.playable ? `${overview.questions_per_level} questions` : 'No questions yet'}</div>
                            </div>
                        </div>
                        <div className="shrink-0">
                            {lv.my_best > 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: ARC.green }}><Crown className="h-3.5 w-3.5" /> {lv.my_best}</span>
                            ) : !lv.playable ? <Lock className="h-4 w-4 text-white/30" /> : <ChevronRight className="h-5 w-5" style={{ color: ARC.green }} />}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function PlayScreen({ session, muted, setMuted, onFinished, onQuit, mascotUrl }) {
    const { user } = useAuth();
    const total = session.total_questions;
    const timeLimit = session.time_limit_sec;
    const levelPoints = session.points_per_question;

    const [index, setIndex] = useState(session.index);
    const [question, setQuestion] = useState(session.question);
    const [score, setScore] = useState(0);
    const [attemptPoints, setAttemptPoints] = useState(0);
    const [phase, setPhase] = useState('reading');       // reading | answering | confirming | result
    const [picked, setPicked] = useState(null);
    const [pendingMs, setPendingMs] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [wasTimeout, setWasTimeout] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timeLimit);
    const [busy, setBusy] = useState(false);
    const [speaking, setSpeaking] = useState(false);

    // Point 26: language is driven by the ONE dropdown in the app header (no in-game picker).
    const [headerLang] = useCourseLang();
    const [textLang, setTextLang] = useState(headerLang || 'en');
    const [voiceLang, setVoiceLang] = useState(headerLang || 'en');
    useEffect(() => { setTextLang(headerLang || 'en'); setVoiceLang(headerLang || 'en'); }, [headerLang]);
    const [phrases, setPhrases] = useState(FALLBACK_PHRASES);
    const [transMap, setTransMap] = useState({});         // `${qid}:${lang}` -> {question,options,explanation}

    const tickRef = useRef(null);
    const deadlineRef = useRef(0);
    const audioRef = useRef(null);
    const mutedRef = useRef(muted);
    const narrationRef = useRef(0);                       // token to ignore stale narration
    const pickedRef = useRef(null);                       // current pick (timer keeps running behind lock popup)
    useEffect(() => { mutedRef.current = muted; if (muted && audioRef.current) audioRef.current.pause(); }, [muted]);

    // ---- translation helper (cached) ----
    const ensureTrans = useCallback(async (qid, lang) => {
        if (lang === 'en') return null;
        const k = `${qid}:${lang}`;
        if (transMap[k]) return transMap[k];
        try {
            const r = await api.post('/games/pulse-power/translate', { question_id: qid, lang });
            setTransMap((m) => ({ ...m, [k]: r.data }));
            return r.data;
        } catch { return null; }
    }, [transMap]);

    // Display question/options in textLang (falls back to English instantly while loading).
    const dispT = textLang !== 'en' ? transMap[`${question.id}:${textLang}`] : null;
    const dispQuestion = dispT?.question || question.question;
    const dispOptions = (dispT?.options && dispT.options.length === question.options.length) ? dispT.options : question.options;

    // Load phrases when the voice language changes.
    useEffect(() => {
        let alive = true;
        if (voiceLang === 'en') { setPhrases(FALLBACK_PHRASES); return; }
        api.get(`/games/pulse-power/phrases?lang=${voiceLang}`)
            .then((r) => { if (alive) setPhrases({ ...FALLBACK_PHRASES, ...r.data }); })
            .catch(() => { if (alive) setPhrases(FALLBACK_PHRASES); });
        return () => { alive = false; };
    }, [voiceLang]);

    // Pre-fetch display translation when textLang or question changes.
    useEffect(() => { if (textLang !== 'en') ensureTrans(question.id, textLang); }, [textLang, question.id, ensureTrans]);

    // ---- audio ----
    const stopAudio = () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} } setSpeaking(false); };

    const ttsPlay = useCallback(async (text, onEnd) => {
        if (mutedRef.current || !text) { onEnd && onEnd(); return; }
        const token = ++narrationRef.current;
        try {
            const r = await api.post('/games/pulse-power/tts', { text }, { responseType: 'blob' });
            if (mutedRef.current || token !== narrationRef.current) { onEnd && onEnd(); return; }
            const url = URL.createObjectURL(r.data);
            stopAudio();
            const a = new Audio(url);
            audioRef.current = a;
            setSpeaking(true);
            a.onended = () => { setSpeaking(false); if (token === narrationRef.current) onEnd && onEnd(); };
            a.onerror = () => { setSpeaking(false); onEnd && onEnd(); };
            await a.play();
        } catch (_) { setSpeaking(false); onEnd && onEnd(); }
    }, []);

    // ---- timer ----
    const stopTimer = () => clearInterval(tickRef.current);
    const startTimer = useCallback((seconds) => {
        stopTimer();
        deadlineRef.current = Date.now() + seconds * 1000;
        let lastSec = Math.ceil(seconds);
        tickRef.current = setInterval(() => {
            const left = Math.max(0, (deadlineRef.current - Date.now()) / 1000);
            setTimeLeft(left);
            const sec = Math.ceil(left);
            if (sec !== lastSec && sec > 0) { lastSec = sec; if (!mutedRef.current) playTick(left <= 5); }
            if (left <= 0) { stopTimer(); onTimeout(); }
        }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // On each new question: the timer + option selection start IMMEDIATELY and the
    // host narrates in parallel (narration never pauses the clock or blocks answering).
    useEffect(() => {
        setPhase('answering');
        setTimeLeft(timeLimit);
        setPicked(null);
        pickedRef.current = null;
        setFeedback(null);
        setWasTimeout(false);
        setBusy(false);
        startTimer(timeLimit);
        let cancelled = false;
        (async () => {
            let voiceText = question.question;
            if (voiceLang !== 'en') {
                const t = await ensureTrans(question.id, voiceLang);
                if (t?.question) voiceText = t.question;
            }
            if (cancelled) return;
            ttsPlay(voiceText);
        })();
        return () => { cancelled = true; stopTimer(); stopAudio(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    useEffect(() => () => { stopTimer(); stopAudio(); }, []);

    const onPick = (i) => {
        if (phase !== 'answering' || busy) return;
        // Point 7: DO NOT stop the timer — it keeps running behind the lock popup.
        pickedRef.current = i;
        setPicked(i);
        setPhase('confirming');
        ttsPlay(phrases.lock);
    };

    const onChange = () => {
        stopAudio();
        pickedRef.current = null;
        setPicked(null);
        setPhase('answering');     // timer is still running underneath
    };

    function onTimeout() {
        stopAudio();
        const p = pickedRef.current;
        if (p != null) {
            // Time ran out while the lock popup was open → lock the selected answer.
            submitAnswer(p, timeLimit * 1000, false);
        } else {
            setWasTimeout(true);
            submitAnswer(-1, timeLimit * 1000, true);
        }
    }

    const submitAnswer = async (selectedIndex, answerMs, timeout = false) => {
        if (busy) return;
        setBusy(true);
        stopTimer();
        stopAudio();
        try {
            const r = await api.post('/games/pulse-power/answer', {
                attempt_id: session.attempt_id,
                selected_index: selectedIndex >= 0 ? selectedIndex : null,
                answer_ms: Math.round(answerMs),
            });
            setScore(r.data.running_score);
            setAttemptPoints(r.data.attempt_points);
            const fb = {
                correct: r.data.correct, correct_index: r.data.correct_index,
                explanation: r.data.explanation, points_awarded: r.data.points_awarded,
                multiplier: r.data.multiplier, status: r.data.status, next_question: r.data.next_question,
                next_index: r.data.index, total_score: r.data.total_score, finalize: r.data.finalize,
            };
            setFeedback(fb);
            setPhase('result');
            setBusy(false);
            // Host reaction voice.
            const kind = timeout ? 'timeout' : (fb.correct ? 'correct' : 'wrong');
            ttsPlay(phrases[kind]);
            // Make sure the explanation is available in the display language.
            if (textLang !== 'en') ensureTrans(question.id, textLang);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Submission failed');
            setBusy(false);
        }
    };

    const next = () => {
        if (!feedback) return;
        stopAudio();
        if (feedback.status === 'active' && feedback.next_question) {
            setIndex(feedback.next_index);
            setQuestion(feedback.next_question);
        } else {
            onFinished({
                status: feedback.status, total_score: feedback.total_score,
                level_best: feedback.finalize?.level_best, improved: feedback.finalize?.improved,
                points_added: feedback.finalize?.points_added, answered: total, total,
            });
        }
    };

    const timePct = (timeLeft / timeLimit) * 100;
    const danger = phase === 'answering' && timeLeft <= 5;

    // Explanation in display language for the result popup.
    const resultExplanation = (textLang !== 'en' ? (transMap[`${question.id}:${textLang}`]?.explanation) : null) || feedback?.explanation;

    return (
        <div className="relative rounded-3xl p-4 sm:p-7 text-white overflow-hidden min-h-[80vh]"
            style={{ background: ARC_BG, boxShadow: 'inset 0 0 0 1px rgba(0,219,121,0.22)' }}
            data-testid="pulse-play-screen">
            <div className="pointer-events-none absolute inset-x-0 -top-24 h-64 blur-3xl rounded-full" style={{ background: 'rgba(0,219,121,0.12)' }} />

            {/* HUD */}
            <div className="relative flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 text-xs font-bold">
                    <span className="px-2.5 py-1 rounded-full" style={{ background: ARC.green, color: ARC.black }}>Level {session.level}</span>
                    <span className="px-2.5 py-1 rounded-full bg-white/10">Q {index + 1} / {total}</span>
                </div>
                <div className="inline-flex items-center gap-2 text-sm">
                    <button onClick={() => setMuted((m) => !m)} data-testid="pulse-mute-btn"
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition" title={muted ? 'Unmute host' : 'Mute host'}>
                        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </button>
                    <button onClick={onQuit} data-testid="pulse-quit-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold transition"
                        style={{ background: ARC.red, boxShadow: '0 4px 14px rgba(255,88,82,0.4)' }}>
                        <LogOut className="h-3.5 w-3.5" /> Quit
                    </button>
                </div>
            </div>

            {/* Hot seat — host + player in focus, compact timer between them */}
            <div className="relative mt-5 flex items-center justify-between gap-2 sm:gap-3">
                {/* Player (left) */}
                <div className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full p-[3px]" style={{ background: `linear-gradient(135deg, ${ARC.mint}, ${ARC.green})` }}>
                        <span className="w-full h-full rounded-full overflow-hidden flex items-center justify-center font-black text-3xl" style={{ background: ARC.forest, color: ARC.green }} data-testid="pulse-player-avatar">
                            {user?.avatar_url
                                ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                                : (user?.full_name?.[0] || 'U')}
                        </span>
                    </div>
                    <span className="mt-1.5 text-[11px] font-bold truncate max-w-[90px]" style={{ color: ARC.green }}>{user?.full_name?.split(' ')[0] || 'You'}</span>
                </div>

                <CircularTimer pct={timePct} seconds={Math.ceil(timeLeft)} danger={danger} />

                {/* Voltmaster host (right) */}
                <div className="flex flex-col items-center">
                    <motion.div
                        animate={speaking ? { scale: [1, 1.06, 0.98, 1.05, 1] } : { scale: 1 }}
                        transition={speaking ? { duration: 0.5, repeat: Infinity } : { duration: 0.2 }}
                        className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full p-[3px]"
                        style={{ background: `linear-gradient(135deg, ${ARC.green}, ${ARC.mint})`, boxShadow: speaking ? `0 0 26px ${ARC.green}aa` : 'none' }}>
                        <img src={mascotUrl ? mediaUrl(mascotUrl) : mascot} alt="Voltmaster host" className="w-full h-full rounded-full object-cover bg-white" data-testid="pulse-mascot" />
                        {speaking && (
                            <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 flex items-end gap-0.5 h-4 px-1.5 rounded-full" style={{ background: ARC.black }} data-testid="pulse-mascot-speaking">
                                {[0, 1, 2, 3].map((b) => (
                                    <motion.span key={b} className="w-1 rounded-full" style={{ background: ARC.green }}
                                        animate={{ height: [4, 15, 7, 13, 4] }}
                                        transition={{ duration: 0.5, repeat: Infinity, delay: b * 0.09 }} />
                                ))}
                            </span>
                        )}
                    </motion.div>
                    <span className="mt-1.5 text-[11px] font-bold" style={{ color: ARC.green }}>Voltmaster</span>
                </div>
            </div>

            {/* Points banner + question (green-bordered hexagon on deep forest) */}
            <div className="relative mt-6 mx-auto max-w-2xl">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] sm:text-xs font-black uppercase tracking-wider mb-2" style={{ background: 'rgba(0,0,0,0.4)', boxShadow: `inset 0 0 0 1px ${ARC.green}66` }}>
                    <span style={{ color: ARC.green }}>For {levelPoints} pts</span>
                </div>
                <motion.div key={question.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className="relative p-[2.5px] drop-shadow-[0_6px_18px_rgba(0,0,0,0.45)]"
                    style={{ clipPath: HEX_Q, background: `linear-gradient(180deg, ${ARC.green}, ${ARC.grass} 60%, #0f6b3f)` }}
                    data-testid="pulse-question">
                    <div className="relative px-10 sm:px-12 py-4 sm:py-5 text-center" style={{ clipPath: HEX_Q, background: 'linear-gradient(180deg,#024d37 0%,#013022 50%,#001710 100%)' }}>
                        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent" />
                        <span className="relative block font-display font-bold text-white text-base sm:text-xl leading-snug">
                            <span style={{ color: ARC.green }}>Q{index + 1}:</span> {dispQuestion}
                        </span>
                        {question.image_url && (
                            <img src={mediaUrl(question.image_url)} alt="" data-testid="pulse-question-image"
                                className="relative mt-3 mx-auto max-h-40 sm:max-h-48 object-contain rounded-xl bg-black/30 p-1.5"
                                style={{ boxShadow: `inset 0 0 0 1px ${ARC.green}55` }} />
                        )}
                    </div>
                </motion.div>
            </div>


            {/* KBC cylindrical hexagon options */}
            <div className="grid sm:grid-cols-2 gap-3.5 sm:gap-4 mt-6 max-w-2xl mx-auto">
                {dispOptions.map((opt, i) => {
                    const isResult = phase === 'result';
                    let state = 'idle';
                    if (isResult && feedback) {
                        if (feedback.correct) {
                            state = i === feedback.correct_index ? 'correct' : 'dim';
                        } else {
                            // Wrong answer: reveal ONLY the picked (wrong) option — never show the
                            // correct one. Just say WRONG and move ahead.
                            state = i === picked ? 'wrong' : 'dim';
                        }
                    } else if (i === picked && phase === 'confirming') state = 'selected';
                    return (
                        <OptionCard key={i} index={i} text={opt} state={state}
                            interactable={phase === 'answering'}
                            onClick={() => onPick(i)} />
                    );
                })}
            </div>

            {/* Point 12: Change / Lock It pinned to a FIXED, always-visible bar so it's easy to
                see & tap on mobile. Portaled to <body> so `fixed` is relative to the viewport
                (a transformed framer-motion ancestor would otherwise anchor it to the container). */}
            {createPortal(
                <AnimatePresence>
                    {phase === 'confirming' && picked != null && (
                        <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                            className="fixed inset-x-0 bottom-0 z-[2147483000] px-3 pb-4 pt-6"
                            style={{ background: 'linear-gradient(180deg, rgba(1,20,14,0) 0%, rgba(1,20,14,0.88) 35%, rgba(1,20,14,0.98) 100%)' }}
                            data-testid="pulse-confirm-bar">
                            <div className="max-w-2xl mx-auto rounded-2xl p-3 sm:p-3.5"
                                style={{ background: 'rgba(2,42,31,0.97)', boxShadow: `0 -8px 26px rgba(0,0,0,0.55), inset 0 0 0 1px ${ARC.green}66` }}>
                                <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-white/70 text-center">
                                    You selected <span style={{ color: ARC.green }}>{LETTERS[picked]}: {dispOptions[picked]}</span> — lock it in?
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-2.5">
                                    <KbcButton variant="dark" onClick={onChange} testid="pulse-change-btn">Change</KbcButton>
                                    <KbcButton variant="lock" onClick={() => submitAnswer(picked, Math.round((timeLimit - timeLeft) * 1000))} disabled={busy} testid="pulse-lockit-btn">
                                        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Lock It'}
                                    </KbcButton>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            {/* Point 18: NO result popup. The picked/correct options are highlighted
                green/red on the screen; this bottom-pinned bar (portaled to body, like the
                confirm bar) just shows the outcome + explanation and a proceed button. */}
            {createPortal(
                <AnimatePresence>
                    {phase === 'result' && feedback && (
                        <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                            className="fixed inset-x-0 bottom-0 z-[2147483000] px-3 pb-4 pt-6"
                            style={{ background: 'linear-gradient(180deg, rgba(1,20,14,0) 0%, rgba(1,20,14,0.9) 40%, rgba(1,20,14,0.99) 100%)' }}
                            data-testid="pulse-result-bar">
                            <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden"
                                style={{ boxShadow: `0 -8px 26px rgba(0,0,0,0.55), inset 0 0 0 1.5px ${feedback.correct ? ARC.grass : ARC.red}` }}>
                                <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: feedback.correct ? ARC.grass : ARC.red }}>
                                    {feedback.correct
                                        ? <CheckCircle2 className="h-5 w-5" style={{ color: ARC.white }} />
                                        : <XCircle className="h-5 w-5" style={{ color: ARC.white }} />}
                                    <span className="font-display font-black tracking-wide" style={{ color: ARC.white }}>
                                        {feedback.correct ? 'CORRECT!' : (wasTimeout ? "TIME'S UP" : 'WRONG')}
                                    </span>
                                    {feedback.correct && <span className="ml-auto font-bold text-sm" style={{ color: ARC.white }} data-testid="pulse-result-points">+{feedback.points_awarded} pts</span>}
                                </div>
                                <div className="p-3.5" style={{ background: 'rgba(2,42,31,0.98)' }}>
                                    {feedback.correct && resultExplanation && (
                                        <p className="text-sm text-white/90 leading-relaxed mb-3" data-testid="pulse-result-explanation">{resultExplanation}</p>
                                    )}
                                    <KbcButton variant={feedback.correct ? 'go' : 'neutral'} onClick={next} testid="pulse-next-btn" full>
                                        {feedback.status === 'active' ? 'Next Question' : 'See Result'} <ChevronRight className="h-5 w-5" />
                                    </KbcButton>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
}

function OptionCard({ index, text, state, interactable, onClick }) {
    // Rounded pill options (Point 14) with a small connector line on the left (KBC-style).
    // Point 13: the A/B/C/D letter no longer sits inside a dark-green hexagon badge.
    const body = {
        idle: `linear-gradient(180deg, ${ARC.white} 0%, #eafff6 55%, ${ARC.mint} 100%)`,
        selected: `linear-gradient(180deg, #4dffa8 0%, ${ARC.green} 55%, #00b866 100%)`,
        correct: `linear-gradient(180deg, #5ef29a 0%, ${ARC.grass} 55%, #1e7a00 100%)`,
        wrong: `linear-gradient(180deg, #ff8a86 0%, ${ARC.red} 55%, #c62b26 100%)`,
        dim: `linear-gradient(180deg, #dfeee8 0%, #c3d8ce 100%)`,
    }[state];
    const border = {
        idle: `linear-gradient(180deg, ${ARC.green}, ${ARC.grass})`,
        selected: `linear-gradient(180deg, #ffffff, ${ARC.green})`,
        correct: `linear-gradient(180deg, #bff5d5, ${ARC.grass})`,
        wrong: `linear-gradient(180deg, #ffd0ce, ${ARC.red})`,
        dim: 'linear-gradient(180deg, #b9cfc5, #93aca1)',
    }[state];
    // Dark text on the light idle/dim states; white text on the coloured states.
    const darkText = state === 'idle' || state === 'dim';
    return (
        <button onClick={onClick} disabled={!interactable} data-testid={`pulse-option-${index}`}
            className={`relative block w-full transition-transform drop-shadow-[0_5px_14px_rgba(0,0,0,0.35)]
                ${interactable ? 'hover:scale-[1.015] active:scale-[0.99]' : ''} ${state === 'dim' ? 'opacity-60' : ''}`}>
            <span className="block p-[2px]" style={{ clipPath: HEX_O, background: border }}>
                <span className="relative flex items-center gap-2.5 pl-7 pr-9 py-3.5" style={{ clipPath: HEX_O, background: body }}>
                    <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent" />
                    <span className="relative flex-shrink-0 font-display font-black text-base sm:text-lg" style={{ color: darkText ? ARC.grass : ARC.white }}>
                        {LETTERS[index]}:
                    </span>
                    <span className="relative text-left text-sm sm:text-[15px] flex-1 font-semibold" style={{ color: darkText ? ARC.forest : ARC.white }}>{text}</span>
                    {state === 'correct' && <CheckCircle2 className="relative h-5 w-5" style={{ color: ARC.white }} />}
                    {state === 'wrong' && <XCircle className="relative h-5 w-5" style={{ color: ARC.white }} />}
                </span>
            </span>
        </button>
    );
}

function KbcButton({ children, onClick, variant = 'neutral', disabled, full, testid }) {
    const styleMap = {
        lock: { background: `linear-gradient(180deg, #ff7a76, ${ARC.red})`, color: ARC.white, boxShadow: '0 0 22px rgba(255,88,82,0.55)' },
        dark: { background: '#023524', color: ARC.white, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14)' },
        go: { background: `linear-gradient(180deg, #4dffa8, ${ARC.green})`, color: ARC.black, boxShadow: '0 0 22px rgba(0,219,121,0.5)' },
        neutral: { background: `linear-gradient(180deg, #4dffa8, ${ARC.green})`, color: ARC.black, boxShadow: '0 0 18px rgba(0,219,121,0.45)' },
    }[variant];
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testid} style={styleMap}
            className={`${full ? 'w-full' : ''} inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-2xl font-display font-extrabold text-base transition active:scale-[0.98] disabled:opacity-60`}>
            {children}
        </button>
    );
}

function CircularTimer({ pct, seconds, danger }) {
    const R = 34, C = 2 * Math.PI * R;
    return (
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center shrink-0" data-testid="pulse-timer">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
                <circle cx="40" cy="40" r={R} fill="none" strokeWidth="7" strokeLinecap="round"
                    stroke={danger ? ARC.red : ARC.green} strokeDasharray={C}
                    strokeDashoffset={C * (1 - pct / 100)} className="transition-all duration-100" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`font-display font-extrabold text-lg sm:text-xl ${danger ? 'animate-pulse' : 'text-white'}`} style={danger ? { color: ARC.red } : undefined}>{seconds}</span>
                <span className="text-[8px] font-bold text-white/40 uppercase">sec</span>
            </div>
        </div>
    );
}

function ResultScreen({ session, onReplay, onLevels }) {
    const s = session.summary;
    return (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl p-7 text-center text-white" style={{ background: ARC_BG, boxShadow: `inset 0 0 0 1px ${ARC.green}44` }} data-testid="pulse-result-screen">
            <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(0,219,121,0.15)' }}>
                <Trophy className="h-8 w-8" style={{ color: ARC.green }} />
            </div>
            <h2 className="font-display font-extrabold text-2xl mt-4">Level {session.level} complete!</h2>

            <div className="grid grid-cols-3 gap-3 mt-6 max-w-md mx-auto">
                <Stat label="This attempt" value={`${s.total_score}`} testid="pulse-result-score" />
                <Stat label="Level best" value={`${s.level_best}`} accent={s.improved} />
                <Stat label="Points added" value={`+${s.points_added}`} />
            </div>
            {s.improved && <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{ color: ARC.green }}><Award className="h-4 w-4" /> New personal best for this level!</div>}

            <div className="flex items-center justify-center gap-3 mt-7">
                <button onClick={onReplay} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold" style={{ background: ARC.green, color: ARC.black }} data-testid="pulse-replay-btn">
                    <RotateCcw className="h-4 w-4" /> Replay level
                </button>
                <button onClick={onLevels} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold bg-white/10 text-white hover:bg-white/20" data-testid="pulse-levels-btn">
                    Choose level
                </button>
            </div>
        </motion.div>
    );
}

function Stat({ label, value, accent, testid }) {
    return (
        <div className="rounded-2xl p-3" style={accent ? { background: 'rgba(0,219,121,0.16)', boxShadow: `inset 0 0 0 1px ${ARC.green}` } : { background: 'rgba(255,255,255,0.06)' }}>
            <div className="font-display font-extrabold text-2xl text-white" data-testid={testid}>{value}</div>
            <div className="text-[10px] uppercase tracking-wide text-white/50 font-bold mt-0.5">{label}</div>
        </div>
    );
}
